/**
 * Ring chart — % donut with the value rendered as italic-display in
 * the centre. Used on Home (score hero, 120px) and the post-rating
 * aggregate panel (160px).
 *
 * Animates the dashoffset on first paint via the SVG's stroke-dasharray
 * transition (1s cubic-bezier). No JS animation lib needed.
 */
type Props = {
  pct: number;             // 0..100
  size?: number;           // diameter px
  stroke?: number;         // ring thickness
  color?: string;          // CSS colour for the value arc
  trackColor?: string;     // CSS colour for the background ring
  label?: string;          // small eyebrow under the number
};

export function RingChart({
  pct,
  size = 120,
  stroke = 10,
  color = 'var(--coral)',
  trackColor = 'var(--line-strong)',
  label,
}: Props) {
  const clamped = Math.max(0, Math.min(100, pct));
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const dash = c * (clamped / 100);
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={`${dash} ${c}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s cubic-bezier(.2,.8,.3,1)' }}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          className="serif"
          style={{
            fontSize: size * 0.32,
            lineHeight: 1,
            fontWeight: 500,
            fontStyle: 'italic',
          }}
        >
          {Math.round(clamped)}
          <span style={{ fontSize: size * 0.15 }}>%</span>
        </div>
        {label && (
          <div
            className="mono"
            style={{
              fontSize: 10,
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
              letterSpacing: 1,
              marginTop: 4,
            }}
          >
            {label}
          </div>
        )}
      </div>
    </div>
  );
}
